import platform from 'platform';
import type { MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FaRegPlayCircle } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useGetCertificationLogByIdMutation,
  useGetQuestionsParsedMutation,
  useGetQuizLogMutation,
  useSendMediaMutation,
  useStartQuizMutation,
  useUpdateIdentityVerificationMutation,
} from '../../features/exam/certificationApiSlice';
import { resolveMonitoringRuntimeConfig } from '../../features/exam/config/monitoringConfig';
import { setQuestions } from '../../features/exam/examSlice';
import {
  ExamValidationSummary,
  InExamCheckStatus,
  ValidationIncident,
} from '../../features/exam/guard/types';
import type { PendingIdentityVerification } from '../../modules/identity-verification/types';
import {
  Certification,
  CertificationType,
  LoadEnrollOutput,
} from '../../types/certifications/Certification';
import { CameraComponent, type CameraControlHandle } from '../Camera';
import { Loading } from '../Loading';
import { Ping } from '../Ping';
import { Timer } from '../Timer';
import { FormQuestion } from './FormQuestion';

type PropsQuestion = {
  certificationLog?: LoadEnrollOutput;
  certification?: Certification;
  type?: CertificationType | null;
  cameraRefreshKey?: number;
  validationSummary: ExamValidationSummary;
  canFinishExam: boolean;
  monitoringStatusLabel: string;
  validationStatus: InExamCheckStatus;
  openIncident: ValidationIncident | null;
  forceTerminationIncident: ValidationIncident | null;
  latestIncident: ValidationIncident | null;
  incidents: ValidationIncident[];
  focusLossCount: number;
  focusLossLimit: number;
  remainingFocusAllowance: number;
  isLivenessChallengeActive?: boolean;
  pendingIdentityVerification?: PendingIdentityVerification | null;
  onStreamReady?: (stream: MediaStream) => void;
  onExamFinalized?: () => void;
  cameraControlRef?: MutableRefObject<CameraControlHandle | null>;
};

function getInfractionCardClassName(
  focusLossCount: number,
  latestIncident: ValidationIncident | null,
) {
  if (latestIncident?.status === 'terminated' || focusLossCount >= 3) {
    return 'border-red-200 bg-gradient-to-br from-white via-red-50 to-red-100';
  }

  if (focusLossCount >= 1) {
    return 'border-amber-200 bg-gradient-to-br from-white via-amber-50 to-amber-100';
  }

  return 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100';
}

function getInfractionMeterClassName(
  focusLossCount: number,
  latestIncident: ValidationIncident | null,
) {
  if (latestIncident?.status === 'terminated' || focusLossCount >= 3) {
    return 'bg-red-500';
  }

  if (focusLossCount >= 1) {
    return 'bg-amber-500';
  }

  return 'bg-slate-900';
}

function latestIncidentLabel(latestIncident: ValidationIncident | null) {
  if (!latestIncident) {
    return 'Nenhuma infração crítica registrada no momento.';
  }

  if (latestIncident.status === 'resolved' && latestIncident.resolvedAt) {
    return `${latestIncident.reasonLabel} resolvida às ${new Date(
      latestIncident.resolvedAt,
    ).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}.`;
  }

  if (latestIncident.status === 'terminated') {
    return `${latestIncident.reasonLabel}. Encerramento automático em andamento.`;
  }

  return `${latestIncident.reasonLabel}. Correção necessária antes de seguir.`;
}

function getOccurrenceTitle(
  openIncident: ValidationIncident | null,
  latestIncident: ValidationIncident | null,
) {
  if (openIncident) return 'Correção necessária';
  if (latestIncident) return 'Última ocorrência';
  return 'Sem bloqueios';
}

function getOccurrenceSummary(
  openIncident: ValidationIncident | null,
  latestIncident: ValidationIncident | null,
) {
  if (openIncident) return openIncident.message;

  if (!latestIncident) {
    return 'Nenhuma infração crítica registrada no momento.';
  }

  if (latestIncident.status === 'resolved') {
    return latestIncidentLabel(latestIncident);
  }

  return `Última ocorrência: ${latestIncident.reasonLabel}. Não há bloqueio ativo no momento.`;
}

function formatIncidentTimestamp(timestamp: string) {
  return new Date(timestamp).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Question({
  certification,
  certificationLog,
  type,
  cameraRefreshKey = 0,
  validationSummary,
  canFinishExam,
  monitoringStatusLabel,
  validationStatus,
  openIncident,
  forceTerminationIncident,
  latestIncident,
  incidents,
  focusLossCount,
  focusLossLimit,
  remainingFocusAllowance,
  isLivenessChallengeActive = false,
  pendingIdentityVerification,
  onStreamReady,
  onExamFinalized,
  cameraControlRef,
}: PropsQuestion) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const hasStartedExamRef = useRef(false);
  const isMountedRef = useRef(true);
  // Tailwind `lg` breakpoint = 1024px. We mount the camera preview in only
  // one layout at a time — mobile OR desktop — to avoid instantiating two
  // parallel MediaRecorder pipelines that would each upload periodic clips
  // (producing duplicate uploads).
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });
  const [finalize, setFinalize] = useState(false);
  const [load, setLoad] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [startedQuizLogId, setStartedQuizLogId] = useState<string | null>(null);

  const [startQuiz] = useStartQuizMutation();
  const [updateIdentityVerification] = useUpdateIdentityVerificationMutation();
  const [getCertificationLogById] = useGetCertificationLogByIdMutation();
  const [sendMedia] = useSendMediaMutation();
  const [getQuizLog, { data: quizLog }] = useGetQuizLogMutation();
  const [getQuestions, { data: questions }] = useGetQuestionsParsedMutation();

  const monitoringType = useMemo(() => {
    if (type) return type;
    return certification?.monitoring ?? null;
  }, [certification?.monitoring, type]);
  const { webcamCaptureDurationSeconds, webcamCaptureIntervalMinutes } =
    useMemo(
      () => resolveMonitoringRuntimeConfig(certification),
      [certification],
    );
  const hasLiveCameraPreview =
    monitoringType === 'video' ||
    monitoringType === 'video_liveness' ||
    monitoringType === 'photo';
  const focusProgress =
    focusLossLimit > 0
      ? Math.min((focusLossCount / focusLossLimit) * 100, 100)
      : 0;
  const infractionCardClassName = getInfractionCardClassName(
    focusLossCount,
    latestIncident,
  );
  const infractionMeterClassName = getInfractionMeterClassName(
    focusLossCount,
    latestIncident,
  );
  const activeIncidentSummary = getOccurrenceSummary(
    openIncident,
    latestIncident,
  );
  const occurrenceTitle = getOccurrenceTitle(openIncident, latestIncident);
  const recentIncidents = useMemo(
    () => incidents.slice(-3).reverse(),
    [incidents],
  );
  const contentGridClassName = hasLiveCameraPreview
    ? 'mt-4 grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]'
    : 'mt-4';
  const mobileStatusGridClassName = hasLiveCameraPreview
    ? 'grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] grid-rows-[minmax(0,1fr)_minmax(0,1fr)]'
    : 'grid-cols-2';

  async function loadQuizContent(quizLogId: string) {
    const quizLogResponse = await getQuizLog(quizLogId).unwrap();
    const quizLogItem = quizLogResponse[0];

    if (!quizLogItem?.quiz) {
      throw new Error('Quiz log não encontrado');
    }

    const questionsData = await getQuestions(quizLogItem.quiz).unwrap();
    if (!isMountedRef.current) return;

    dispatch(setQuestions({ questions: questionsData }));
  }

  async function handleExam() {
    if (!certificationLog?.certification || hasStartedExamRef.current) return;

    hasStartedExamRef.current = true;

    setLoad(true);
    setBootstrapError(null);
    const appVersion = process.env.REACT_APP_VERSION || '0.0.0';

    try {
      const response = await startQuiz({
        _id: certificationLog._id,
        ...(certification?.monitoring === 'select' &&
          type && { monitoring: type }),
        device: `${platform.name} ${platform.version} on ${platform.os?.family} ${platform.os?.version}`,
        origin: 'web',
        appVersion,
        validationSummary,
      }).unwrap();

      const quizLogId = certificationLog.quiz_log || response.quiz_log;
      if (!isMountedRef.current) return;

      if (pendingIdentityVerification) {
        const logId = certificationLog._id;
        const pending = pendingIdentityVerification;
        (async () => {
          try {
            let imageUrl = '';
            if (pending.imageDataUrl) {
              try {
                const resp = await fetch(pending.imageDataUrl);
                const blob = await resp.blob();
                const formData = new FormData();
                formData.append(
                  'file',
                  blob,
                  `identity_${pending.certificationLogId}.jpg`,
                );
                formData.append(
                  'extra',
                  JSON.stringify({
                    session: 'identity_verification',
                    type: 'image',
                    certification_log: pending.certificationLogId,
                  }),
                );
                const uploadResult = await sendMedia(formData).unwrap();
                imageUrl = uploadResult?.uploads?.[0]?.url ?? '';
              } catch {
                // Upload failed — proceed without image URL
              }
            }

            const freshLogs = await getCertificationLogById(logId).unwrap();
            const fullLog = freshLogs?.[0];
            if (!fullLog) return;
            await updateIdentityVerification({
              ...fullLog,
              identity_verification: {
                status: pending.status,
                verification_time: pending.verificationTime,
                imagem_url: imageUrl,
                ...(imageUrl ? { selfie_url: imageUrl } : {}),
              },
            }).unwrap();
          } catch {
            // noop — identity verification data is best-effort
          }
        })();
      }

      setStartedQuizLogId(quizLogId);
      await loadQuizContent(quizLogId);
    } catch (error) {
      const fallbackQuizLogId = certificationLog?.quiz_log || null;

      if (fallbackQuizLogId) {
        try {
          setStartedQuizLogId(fallbackQuizLogId);
          await loadQuizContent(fallbackQuizLogId);
          return;
        } catch {
          // fallback falhou, trata erro abaixo
        }
      }

      hasStartedExamRef.current = false;
      if (isMountedRef.current) {
        setBootstrapError(
          'Não foi possível carregar a prova agora. Atualize a página e tente novamente.',
        );
      }
      console.error({ error });
    } finally {
      if (isMountedRef.current) {
        setLoad(false);
      }
    }
  }

  useEffect(() => {
    isMountedRef.current = true;
    handleExam().catch(() => undefined);

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (finalize && onExamFinalized) {
      onExamFinalized();
    }
  }, [finalize, onExamFinalized]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsDesktopViewport(event.matches);
    };
    setIsDesktopViewport(query.matches);
    if (query.addEventListener) {
      query.addEventListener('change', handleChange);
      return () => query.removeEventListener('change', handleChange);
    }
    query.addListener(handleChange);
    return () => query.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!quizLog?.[0]?.finished?.$date) return;

    toast.warn('Exame já finalizado', {
      position: 'top-center',
      autoClose: 15000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    navigate(`/result/${certificationLog!._id}`);
  }, [certificationLog, navigate, quizLog]);

  if (load) {
    return <Loading />;
  }

  if (!questions?.length && startedQuizLogId) {
    return <Loading />;
  }

  if (!questions?.length) {
    if (!bootstrapError) {
      return <Loading />;
    }

    return (
      <div className="mx-auto w-full max-w-[960px] px-4 py-8">
        <div className="rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-600">
            Falha ao carregar a prova
          </p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900">
            Não conseguimos iniciar esta tentativa
          </h2>
          <p className="mt-2 text-sm text-slate-600">{bootstrapError}</p>
          <button
            type="button"
            onClick={() => {
              hasStartedExamRef.current = false;
              handleExam().catch(() => undefined);
            }}
            className="mt-5 rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-800"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1480px] px-3 py-3 sm:px-4 sm:py-4 lg:px-6">
      <div className="sticky top-0 z-30 rounded-2xl border border-slate-200 bg-white/95 px-3 py-2 shadow-sm backdrop-blur lg:px-4 lg:py-3">
        <div className="space-y-1.5 lg:hidden">
          <div
            className={`grid items-stretch gap-1 ${mobileStatusGridClassName}`}
          >
            {hasLiveCameraPreview && !isDesktopViewport && (
              <div className="row-span-2 h-full rounded-xl border border-slate-200 bg-slate-50/80 p-1">
                <div className="mb-1 flex items-center justify-between gap-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Câmera
                  </p>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Ao vivo
                  </span>
                </div>
                <div className="flex justify-center">
                  <div className="aspect-square w-full max-w-[120px] overflow-hidden rounded-lg border border-slate-200 bg-white p-0.5">
                    <CameraComponent
                      key={`exam-camera-mobile-${cameraRefreshKey}`}
                      type={monitoringType}
                      certification_log={certificationLog!._id}
                      captureDuration={webcamCaptureDurationSeconds}
                      captureInterval={webcamCaptureIntervalMinutes}
                      onStreamReady={onStreamReady}
                      isExamFinalized={finalize}
                      isLivenessChallengeActive={isLivenessChallengeActive}
                      controlRef={cameraControlRef}
                      previewWidth="100%"
                      previewHeight="100%"
                      roundedPreview={false}
                      className="h-full w-full"
                    />
                  </div>
                </div>
                {isLivenessChallengeActive && (
                  <p className="mt-1 truncate text-[10px] font-semibold text-amber-700">
                    Desafio ativo
                  </p>
                )}
              </div>
            )}

            <div
              className={`rounded-xl border border-slate-200 bg-slate-50 px-1.5 py-1 ${
                hasLiveCameraPreview ? 'row-start-1 h-full' : ''
              }`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Tempo
              </p>
              <div className="mt-0.5">
                <Timer
                  // @ts-ignore
                  startedTime={new Date(quizLog?.[0]?.started?.$date)}
                  expiryTime={certification!.quiz_duration}
                  onExpireTime={() => setFinalize(true)}
                  variant="micro"
                  showLabel={false}
                  className="whitespace-nowrap"
                />
              </div>
            </div>

            <div
              className={`rounded-xl border px-1.5 py-1 shadow-sm ${infractionCardClassName} ${
                hasLiveCameraPreview ? 'row-start-2 h-full' : ''
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Infrações
                </p>
                <p className="text-[15px] font-semibold text-slate-900">
                  {remainingFocusAllowance}
                </p>
              </div>
              <p className="mt-1 text-[15px] font-semibold text-slate-900">
                {focusLossCount}/{focusLossLimit}
              </p>
              <div className="mt-1 h-1 rounded-full bg-white/80">
                <div
                  className={`h-1 rounded-full transition-all ${infractionMeterClassName}`}
                  style={{ width: `${Math.max(focusProgress, 0)}%` }}
                />
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <p
              className="text-[11px] font-semibold tracking-[0.04em] text-slate-600"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              Prova em andamento · {certification?.title}
            </p>
          </div>
        </div>

        <div className="hidden flex-col gap-3 lg:flex lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              Certificação em andamento
            </p>
            <h1 className="mt-1 max-w-5xl text-xl font-semibold leading-tight tracking-[-0.02em] text-slate-900 sm:text-2xl xl:text-[1.75rem]">
              {certification?.title}
            </h1>
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:min-w-[420px]">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Tempo restante
              </p>
              <div className="mt-1">
                <Timer
                  // @ts-ignore
                  startedTime={new Date(quizLog?.[0]?.started?.$date)}
                  expiryTime={certification!.quiz_duration}
                  onExpireTime={() => setFinalize(true)}
                  variant="compact"
                />
              </div>
            </div>

            <div
              className={`rounded-xl border px-3 py-2 shadow-sm ${infractionCardClassName}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Infrações
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {remainingFocusAllowance} restantes
                </p>
              </div>
              <p className="mt-2 text-sm font-semibold text-slate-900">
                {focusLossCount}/{focusLossLimit} ocorrências
              </p>
              <div className="mt-2 h-1.5 rounded-full bg-white/80">
                <div
                  className={`h-1.5 rounded-full transition-all ${infractionMeterClassName}`}
                  style={{ width: `${Math.max(focusProgress, 0)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Ping certification_log={certificationLog!._id} />

      {quizLog?.[0] && (
        <div className={contentGridClassName}>
          <div className="min-w-0">
            <FormQuestion
              quizLog={quizLog[0]}
              finalize={finalize}
              logId={certificationLog!._id}
              canFinishExam={canFinishExam}
              validationStatus={validationStatus}
              monitoringStatusLabel={monitoringStatusLabel}
              openIncident={openIncident}
              forceTerminationIncident={forceTerminationIncident}
              remainingFocusAllowance={remainingFocusAllowance}
            />
          </div>

          {hasLiveCameraPreview && isDesktopViewport && (
            <aside className="hidden lg:sticky lg:top-[94px] lg:block">
              <div className="min-w-0 overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                      Sua câmera
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <FaRegPlayCircle size={14} /> Monitoramento ao vivo
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.15)]" />
                    Ao vivo
                  </span>
                </div>

                <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-950/5 p-2">
                  <CameraComponent
                    key={`exam-camera-desktop-${cameraRefreshKey}`}
                    type={monitoringType}
                    certification_log={certificationLog!._id}
                    captureDuration={webcamCaptureDurationSeconds}
                    captureInterval={webcamCaptureIntervalMinutes}
                    onStreamReady={onStreamReady}
                    isExamFinalized={finalize}
                    isLivenessChallengeActive={isLivenessChallengeActive}
                    controlRef={cameraControlRef}
                    previewWidth="100%"
                    previewHeight={180}
                    roundedPreview={false}
                    className="w-full"
                  />
                </div>

                <p className="mt-2 text-xs text-slate-500">
                  Mantenha o rosto centralizado e visível.
                </p>
                <div className="mt-3 space-y-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Ocorrência atual
                    </p>
                    <p className="mt-1.5 text-sm font-semibold text-slate-900">
                      {occurrenceTitle}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {activeIncidentSummary}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Prévia de ocorrências
                    </p>

                    {recentIncidents.length === 0 ? (
                      <p className="mt-1.5 text-xs text-slate-600">
                        Nenhuma infração crítica registrada nesta tentativa.
                      </p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {recentIncidents.map(incident => (
                          <div
                            key={`${incident.timestamp}-${incident.checkId}`}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2"
                          >
                            <p className="truncate text-xs font-semibold text-slate-900">
                              {incident.reasonLabel}
                            </p>
                            <p className="mt-1 text-[11px] text-slate-600">
                              Infração {incident.occurrence} às{' '}
                              {formatIncidentTimestamp(incident.timestamp)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {!canFinishExam && (
                    <p className="rounded-2xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                      O envio está bloqueado enquanto houver falha crítica.
                    </p>
                  )}
                </div>
              </div>
            </aside>
          )}
        </div>
      )}
    </div>
  );
}
